/**
 * @file: ActionButton.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A tonal CTA button (primary / secondary / destructive) with an optional
 * leading Lucide glyph, used for the lead's primary and editor actions.
 */

import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { LucideIcon } from 'lucide-react-native';
import { TouchableOpacity } from 'react-native';
import { LD } from '../leadDetailKit';

export const ActionButton = ({
  label,
  tone,
  icon,
  onPress,
}: {
  label: string;
  tone: 'primary' | 'secondary' | 'destructive';
  icon?: LucideIcon;
  onPress: () => void;
}) => {
  const { rpx } = useResponsiveContext();
  const Glyph = icon;
  // The primary CTA owns the weight: a filled, lifted brand button that stays
  // dominant even when the desktop layout sets the two ghost actions beside it.
  const isPrimary = tone === 'primary';
  const bg = isPrimary ? LD.brand : LD.surface;
  const fg = isPrimary ? '#fff' : tone === 'destructive' ? LD.danger : LD.steel;
  const borderColor =
    tone === 'destructive' ? '#E7BAB4' : isPrimary ? undefined : LD.hairline;
  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      style={{
        flexDirection: 'row',
        flex: isPrimary ? 1.3 : 1,
        height: isPrimary ? rpx(52) : rpx(46),
        borderRadius: rpx(13),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: bg,
        borderWidth: isPrimary ? 0 : 1,
        borderColor,
        shadowColor: isPrimary ? LD.brand : undefined,
        shadowOpacity: isPrimary ? 0.22 : 0,
        shadowRadius: isPrimary ? rpx(10) : 0,
        shadowOffset: { width: 0, height: isPrimary ? rpx(3) : 0 },
        elevation: isPrimary ? 2 : 0,
      }}
    >
      {Glyph ? (
        <Glyph size={rpx(17)} color={fg} style={{ marginRight: rpx(7) }} />
      ) : null}
      <Typography.Text size={rpx(15)} color={fg} bold>
        {label}
      </Typography.Text>
    </TouchableOpacity>
  );
};
